<template>
  <div class="select-chart-container" :id="id">
    <div class="position-absolute" style="top: 15px; right: 20px">
      <DownloadChartButton :id="id" />
    </div>
    <v-row>
      <v-col cols="12">
        <ChartTitle :title="title"></ChartTitle>
      </v-col>
      <v-col cols="12">
        <AverageScore
          colorTable="gradientColors"
          :extra-legend-items="annotations.map(a => a.label)"
          :averageScoreOption="true"
          legendItemTitle="PPI"
          :gradientColors="gradientColors"
        ></AverageScore>
      </v-col>

      <v-col cols="12">
        <HorizontalBarChart
          style="height: 1000px"
          v-if="finalChartData"
          :chartData="finalChartData"
          :annotations="annotations"
          :options="chart.options"
          :aspect-ratio="aspectRatio"
          class="bar-chart-plot-full-width"
        >
        </HorizontalBarChart>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
// This file includes a custom title and custom legend
// for bar charts
import HorizontalBarChart from "../base/HorizontalBarChart.vue";
import ChartTitle from "../components/ChartTitle.vue";
import AverageScore from "../components/AverageScore.vue";
import Exporter from "vue-chartjs-exporter";
import { computed } from "vue";
import Utils from "@/Mixins/Utils";
import DownloadChartButton from "@/Charts/components/DownloadChartButton.vue";

const props = defineProps({
  title: {
    type: String,
    default: null,
    required: false,
  },
  chartData: {
    type: Object,
    required: true,
  },
  gradientColors: {
    type: Array,
    required: true,
  },
  annotations: {
    type: Array,
    required: false,
    default: () => {
      return []
    }
  },
  axisLabels: {
    type: Object,
    required: false,
    default() {
      return { x: "", y: "" };
    },
  },
  id: {
    type: String,
    required: false,
  },
});

const finalChartData = computed(() => {
  const result = {}
  result.labels = []

  for (let label of props.chartData.labels) {
    if (label.length > 70) {
    /* Split it */
    result.labels.push(wrapLabel(label))
    } else {
    result.labels.push(label)
    }
  }

  for (let dataset of props.chartData.datasets) {
    dataset.barThickness = 15
    dataset.barPercentage = 0.7
    dataset.categoryPercentage = 0.8
  }

  result.datasets = props.chartData.datasets
  return result
})

function wrapLabel(label) {
  const maxLineLength = 50; // max chars per line
  const words = label.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + word).length <= maxLineLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines;
}


const aspectRatio = computed(() => {
  return Utils.methods.psiChartAspectRatio(props.chartData.labels.length);
});

const chart = computed(() => {
  return {
    animation: { duration: 1 },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 50,
          right: 50,
        }
      },
      indexAxis: "y",
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          stacked: false,
          type: "linear",
          id: "x-axis-1",
          position: "top",
          max: 5,
          ticks: {
            color: '1e1e1e',
            font: {
              size: 15
            }
          },
        },
        'bar-x-axis1': {
          max: 5,
          axis: 'x',
          position: 'bottom',
          grid: {
            drawOnChartArea: false
          },
          ticks: {
            color: '1e1e1e',
            font: {
              size: 15
            }
          }
        },
        y: {
          ticks: {
            color: '1e1e1e',
            autoSkip: false
          }
        }
      },
    },
  };
});

const chartDataCopy = computed(() => {
  return Utils.methods.deepCopy(props.chartData);
});
</script>

